import { useState, type ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { FormError } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Typography } from '@/components/ui/typography';
import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import type { ContactAction } from '../../../types/help-support.types';
import { Textarea } from '@/components/ui/textarea';

interface TextSubmissionModalProps {
  title: string;
  placeholder: string;
  action: ContactAction;
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (action: ContactAction, content: string) => Promise<void>;
}

const TextSubmissionModal = ({
  title,
  placeholder,
  action,
  submitLabel = 'Submit Request',
  onClose,
  onSubmit,
}: TextSubmissionModalProps) => {
  const dispatch = useDispatch();
  const [text, setText] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim()) {
      setIsValid(true);
      return;
    }
    setIsValid(false);
    setIsLoading(true);
    try {
      await onSubmit(action, text);
      onClose();
      dispatch(
        showAlert({ type: Variants.SUCCESS, message: 'Request has been successfully sent.' }),
      );
    } catch (error: unknown) {
      onClose();
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(open) => !open && onClose()}
      title={<Typography variant="heading-lg" className="font-medium">{title}</Typography>}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isLoading}
            onClick={handleSubmit}
            className="w-full sm:w-auto"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="pt-2">
        <div className="mb-1.5 flex items-center justify-between">
          <Label htmlFor="submission-text" required className="mb-0">
            Description
          </Label>
          <span className="text-sm text-muted-foreground">
            {text.length}/350
          </span>
        </div>
        <Textarea
          id="submission-text"
          name="submission-text"
          rows={6}
          maxLength={350}
          placeholder={placeholder}
          value={text}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
          className={isValid ? 'border-destructive' : undefined}
          data-testid="submission-text_id"
        />
        <FormError message={isValid ? 'Please enter description' : undefined} />
      </div>
    </Modal>
  );
};

export default TextSubmissionModal;
